
import type {
    StructArray,
    StructArrayMember
} from '../util/struct_array.ts';
import type {Program} from './program.ts';
import type {Context} from './context.ts';

/**
 * An Enum for AttributeType
 */
const AttributeType = {
    Int8: 'BYTE',
    Uint8: 'UNSIGNED_BYTE',
    Int16: 'SHORT',
    Uint16: 'UNSIGNED_SHORT',
    Int32: 'INT',
    Uint32: 'UNSIGNED_INT',
    Float32: 'FLOAT'
};

/**
 * @internal
 * The `VertexBuffer` class turns a `StructArray` into a WebGL buffer. Each member of the StructArray's
 * Struct type is converted to a WebGL attribute.
 */
export class VertexBuffer {
    length: number;
    attributes: readonly StructArrayMember[];
    itemSize: number;
    dynamicDraw: boolean;
    context: Context;
    buffer: WebGLBuffer;

    /**
     * @param dynamicDraw - Whether this buffer will be repeatedly updated.
     */
    constructor(context: Context, array: StructArray, attributes: readonly StructArrayMember[], dynamicDraw?: boolean) {
        this.length = array.length;
        this.attributes = attributes;
        this.itemSize = array.bytesPerElement;
        this.dynamicDraw = dynamicDraw;

        this.context = context;
        const gl = context.gl;
        this.buffer = gl.createBuffer();
        context.bindVertexBuffer.set(this.buffer);
        gl.bufferData(gl.ARRAY_BUFFER, array.arrayBuffer, this.dynamicDraw ? gl.DYNAMIC_DRAW : gl.STATIC_DRAW);

        if (!this.dynamicDraw) {
            array.freeBufferAfterUpload();
        }
    }

    bind(): void {
        this.context.bindVertexBuffer.set(this.buffer);
    }

    updateData(array: StructArray): void {
        if (array.length !== this.length) throw new Error(`Length of new data is ${array.length}, which doesn't match current length of ${this.length}`);
        const gl = this.context.gl;
        this.bind();
        gl.bufferSubData(gl.ARRAY_BUFFER, 0, array.arrayBuffer);
    }

    enableAttributes(gl: WebGL2RenderingContext, program: Program<any>): void {
        for (const member of this.attributes) {
            const attribute = program.attributes[member.name];
            if (attribute !== undefined) {
                gl.enableVertexAttribArray(attribute.location);
            }
        }
    }

    /**
     * Set the attribute pointers in a WebGL context
     * @param gl - The WebGL context
     * @param program - The active WebGL program
     * @param vertexOffset - Index of the starting vertex of the segment
     */
    setVertexAttribPointers(gl: WebGL2RenderingContext, program: Program<any>, vertexOffset?: number | null): void {
        for (const member of this.attributes) {
            const attribute = program.attributes[member.name];

            if (attribute !== undefined) {
                const offset = member.offset + (this.itemSize * (vertexOffset || 0));
                if (attribute.isInteger) {
                    gl.vertexAttribIPointer(
                        attribute.location,
                        member.components,
                        gl[AttributeType[member.type]],
                        this.itemSize,
                        offset
                    );
                } else {
                    gl.vertexAttribPointer(
                        attribute.location,
                        member.components,
                        gl[AttributeType[member.type]],
                        false,
                        this.itemSize,
                        offset
                    );
                }
            }
        }
    }

    /**
     * Destroy the GL buffer bound to the given WebGL context
     */
    destroy(): void {
        const gl = this.context.gl;
        if (this.buffer) {
            gl.deleteBuffer(this.buffer);
            delete this.buffer;
        }
    }
}
